import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState("");
  const { lang } = useApp();
  const tr = t[lang];
  const locale = lang === "ar" ? "ar-SA" : "en-US";

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    setLoading(true);
    const list = await base44.entities.Customer.list("-created_date", 500);
    setCustomers(list);
    setLoading(false);
  };

  const filtered = customers.filter(c =>
    !searchPhone || c.phone?.includes(searchPhone) || c.email?.includes(searchPhone)
  );

  const thClass = `px-4 py-3 font-bold text-foreground ${lang === "ar" ? "text-right" : "text-left"}`;

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-black text-foreground">{tr.customerManagement}</h1>
              <p className="text-muted-foreground text-sm">{filtered.length} {lang === "ar" ? "عميل" : "customers"}</p>
            </div>
            <button onClick={loadCustomers} className="px-4 py-2 bg-secondary text-foreground rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors">
              🔄 {tr.refresh}
            </button>
          </div>

          {/* Search */}
          <div className="bg-card rounded-2xl p-4 border border-border shadow-sm">
            <input
              type="tel"
              placeholder={`🔍 ${tr.searchByPhone}`}
              value={searchPhone}
              onChange={e => setSearchPhone(e.target.value)}
              className="w-full max-w-xs px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-primary bg-background text-foreground"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary/50 text-sm border-b border-border">
                      <th className={thClass}>{tr.phone}</th>
                      <th className={thClass}>{lang === "ar" ? "البريد الإلكتروني" : "Email"}</th>
                      <th className={thClass}>{tr.totalSpinsCount}</th>
                      <th className={thClass}>{tr.lastSpin}</th>
                      <th className={thClass}>{tr.lastCoupon}</th>
                      <th className={thClass}>{tr.usedCount}</th>
                      <th className={thClass}>{tr.unusedCount}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map(customer => (
                      <tr key={customer.id} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-sm text-foreground" dir="ltr">
                         {customer.phone}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground" dir="ltr">
                         {customer.email || <span className="text-xs text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full font-bold text-sm">
                            {customer.total_spins || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {customer.last_spin_at ? new Date(customer.last_spin_at).toLocaleDateString(locale) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {customer.last_coupon_code ? (
                            <code className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                              {customer.last_coupon_code}
                            </code>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 rounded-full font-bold text-sm">
                            {customer.total_coupons_used || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 rounded-full font-bold text-sm">
                            {customer.total_coupons_unused || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                          <div className="text-4xl mb-2">👥</div>
                          <p>{tr.noCustomers}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}